/**
 * RFC-5322 threading helpers for matching a follow-up email to the application
 * it belongs to (#363).
 *
 * `In-Reply-To` names only the IMMEDIATE parent; `References` carries the whole
 * ancestor chain, which is what survives when someone replies to an older message
 * in a long thread. We check both.
 *
 * Pure — DB lookups live in api/email-inbound.ts.
 */

/** Reply/forward prefixes, including the numbered `Re[2]:` form some clients emit. */
const REPLY_PREFIX = /^\s*(?:re|fw|fwd)\s*(?:\[\d+\])?\s*:\s*/i;

/**
 * Extract normalised `<id@host>` tokens from an In-Reply-To or References value.
 *
 * Bare ids (no angle brackets) get wrapped so comparisons against stored
 * `message_id` values are apples-to-apples. Duplicates are collapsed.
 *
 * @param opts.newestFirst References runs oldest→newest; pass true to try the
 *   closest ancestor first, which is the most likely correct thread.
 */
export function parseMessageIdRefs(
	value: string | null | undefined,
	opts: { newestFirst?: boolean } = {},
): string[] {
	if (!value || !value.trim()) return [];

	const bracketed = value.match(/<[^<>\s]+>/g);
	const raw = bracketed && bracketed.length > 0
		? bracketed
		: value.trim().split(/\s+/).map((t) => `<${t.replace(/^<|>$/g, '')}>`);

	const seen = new Set<string>();
	const out: string[] = [];
	for (const token of raw) {
		const id = token.trim();
		// `<>` and other empties carry no information.
		if (id.length <= 2 || seen.has(id)) continue;
		seen.add(id);
		out.push(id);
	}
	return opts.newestFirst ? out.reverse() : out;
}

/**
 * Reduce a subject to a comparable form: strip stacked reply/forward prefixes,
 * lower-case, collapse whitespace.
 *
 * Returns '' when nothing but prefixes remain — callers MUST refuse to match on an
 * empty subject, or every "Re:" with no body subject would collide.
 */
export function normalizeReplySubject(subject: string | null | undefined): string {
	let s = (subject ?? '').trim();
	// Stacked prefixes: "Re: Fw: RE: …"
	let guard = 0;
	while (REPLY_PREFIX.test(s) && guard < 10) {
		s = s.replace(REPLY_PREFIX, '');
		guard += 1;
	}
	return s.toLowerCase().replace(/\s+/g, ' ').trim();
}
